/**
 * The configurable tab of the budget rollup — a subtree mini-P&L, partitioned by whatever grouping chain
 * the reader asks for (PlanRollup-View-Shaping §5, §9). Defaults to Account Group → GL Account, which is
 * what this view has always shown; from there any chain over org node, account group, account type,
 * account or a financial dimension is one chip away. Rendered as the same nested MUI table with subtotals
 * and collapse as the other rollup views (read-only, Värde only).
 *
 * **Rows arrive sorted, and this component does not re-sort them** (§10). Per-source ordering — BAS
 * account order, org tree order, an enum's declared ordinal — cannot be derived from a display label, so
 * it is the server's to own; `buildRollupRows` follows leaf insertion order by contract.
 *
 * **No Volume column, deliberately.** Quantities are only additive within one unit, and this view spans
 * the whole chart: sold m³fub, purchased m³fub and personnel hours would end up in one column and one
 * total, and the response carries no unit per row for the client to check against. The server does
 * enforce unit homogeneity *per account*, but nothing made the sums across accounts meaningful — the
 * demo tenant read 299 521 as if cubic metres and headcount could be added. Volume belongs on the Plan
 * View pivots, where each measure declares its own unit in the column header ("Volym (m3f)").
 */

import { useMemo, useState } from 'react';
import {
  Alert, Box, CircularProgress, Paper, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { formatNumber } from '@/lib/locale';
import { buildRollupRows, isHiddenByCollapse, RollupLeaf } from './rollupTree';
import { GroupByChips } from './GroupByChips';
import { RollupUnfilteredTotalRow, useFilteredTotalLabel } from './RollupFilteredTotal';
import { EMPTY_SHAPING, shapingQueryKey, shapingRequestFields, type RollupShaping } from './planRollupShaping';

/** The preset this tab opens on — exactly the two levels it rendered before the chain existed. */
export const BY_ACCOUNT_DEFAULT_GROUP_BY = ['ACCOUNT_GROUP', 'ACCOUNT'];

/** One level's value on a row, aligned to the response's axes. */
interface GroupedKey {
  valueCode: string;
  valueName: string;
  /** What the server sorted this level by; the client never re-derives it (§10). */
  sortKey?: string | null;
}
interface GroupedRow {
  key: GroupedKey[];
  amount: number;
}
interface GroupedAxis {
  token: string;
  source: string;
  dimensionDefinitionId?: string | null;
  name: string;
}
interface PlanRollupGroupedDto {
  /** The chain actually applied — an empty request means the preset, so this may differ from what was sent. */
  axes: GroupedAxis[];
  rows: GroupedRow[];
  /** Gross sum of magnitudes — income and cost added together, so not a result. */
  grandTotal: number;
  /** Income minus cost; excluded accounts contribute nothing. Identical for every chain (§14). */
  signedResult: number;
  excludedAmount: number;
  /** True only when a sign was guessed from an ambiguous account type — not merely when a note is shown. */
  signedResultIsApproximate: boolean;
  excludedAccountsAdvisory?: string | null;
  /** Whether a filter or period range narrowed this read — the server's answer, not the request's (§11). */
  isShaped: boolean;
  warnings: string[];
}

// POST, not GET: the filter AST the page toolbar builds has no upper bound and must travel in a body
// rather than a request line. Every rollup read is POST for that reason — see
// PlanBudgetRollupReadController.
const ENDPOINT = '/api/finance/planbudgetrollup/grouped';

/** The server's residue bucket for a line with no value at a level (PlanBudgetRollupAppService). */
const NONE_BUCKET = '__NONE__';

/**
 * Drops a "(none)" level wherever it is the **only** value under its parent — a pass-through node that
 * costs a row and says nothing, since it necessarily carries its parent's whole amount. Group by
 * Account › Supplier › Customer and every purchase row grows a "(none)" customer, which is noise.
 *
 * **Only the pass-through case.** Where "(none)" has siblings it is genuinely informative — it separates
 * the lines that have a supplier from the lines that do not — and it keeps its row. And no line is ever
 * dropped either way: this rewrites how the tuples nest, never which of them exist, so §14's
 * `Σ rows = GrandTotal` holds exactly as before. Removing the lines instead would take money off the
 * table while the footer still counted it, which is the silent failure the "(none)" bucket exists to
 * prevent.
 *
 * A level is never collapsed away entirely: a chain of one level whose every value is "(none)" still
 * renders that row, because "nothing has this dimension" is an answer and a blank table is not.
 */
export function collapsePassThroughNoneLevels(rows: { key: GroupedKey[] }[]): string[][] {
  if (rows.length === 0) return [];
  const depth = rows[0].key.length;

  // For each ancestor path, which codes appear at the next level. A level is redundant *at that path*
  // when the only code there is the residue bucket.
  const codesAtPath = new Map<string, Set<string>>();
  for (const row of rows) {
    for (let level = 0; level < row.key.length; level++) {
      const path = row.key.slice(0, level).map((k) => k.valueCode).join('\u0000');
      const codes = codesAtPath.get(path) ?? new Set<string>();
      codes.add(row.key[level].valueCode);
      codesAtPath.set(path, codes);
    }
  }

  const isRedundant = (row: { key: GroupedKey[] }, level: number) => {
    if (row.key[level].valueCode !== NONE_BUCKET) return false;
    const path = row.key.slice(0, level).map((k) => k.valueCode).join('\u0000');
    return codesAtPath.get(path)?.size === 1;
  };

  return rows.map((row) => {
    const kept = row.key.filter((_, level) => !isRedundant(row, level)).map((k) => k.valueName);
    // Never leave a row with no label at all.
    return kept.length > 0 ? kept : [row.key[depth - 1].valueName];
  });
}

const fmt0 = (v: number | null | undefined) => (v == null ? '' : formatNumber(v, { maximumFractionDigits: 0 }));

interface Props {
  versionId: string;
  orgUnitIds: string[];
  excludeOrgUnitIds?: string[];
  inclusion: 'SUBMITTED' | 'ALL';
  /** The grouping chain, outermost level first. Empty means the server's preset. */
  groupBy?: string[];
  onGroupByChange?: (next: string[]) => void;
  /** The page-level filter and period window — every tab narrows on the same one (§9.1). */
  shaping?: RollupShaping;
  /** The page's unshaped consolidated total, printed beside a filtered one as the reference (§11). */
  unfilteredTotal?: number;
}

export function PlanRollupByAccount({
  versionId,
  orgUnitIds,
  excludeOrgUnitIds = [],
  inclusion,
  groupBy = BY_ACCOUNT_DEFAULT_GROUP_BY,
  onGroupByChange,
  shaping = EMPTY_SHAPING,
  unfilteredTotal,
}: Props) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const filteredLabel = useFilteredTotalLabel();

  const { data, isFetching, isError, error } = useQuery<PlanRollupGroupedDto, Error>({
    queryKey: [
      'planRollupGrouped', versionId, [...orgUnitIds].sort(), [...excludeOrgUnitIds].sort(), inclusion, groupBy,
      shapingQueryKey(shaping),
    ],
    queryFn: async () => {
      return (await apiClient.post<PlanRollupGroupedDto>(ENDPOINT, {
        planVersionId: versionId,
        orgUnitIds,
        excludeOrgUnitIds,
        inclusion,
        groupBy,
        ...shapingRequestFields(shaping),
      })).data;
    },
    enabled: !!versionId && orgUnitIds.length > 0,
  });

  const allRows = useMemo(() => {
    // No sorting here: the server owns per-source ordering (§10) and buildRollupRows follows leaf
    // insertion order by contract. Re-sorting on the display label would silently undo BAS account
    // order the moment the chain contains a level whose label is not its sort key.
    const leaves: RollupLeaf[] = collapsePassThroughNoneLevels(data?.rows ?? []).map((keys, i) => ({
      keys,
      amount: (data?.rows ?? [])[i].amount,
      quantities: [],
    }));
    return buildRollupRows(leaves);
  }, [data]);

  // The chain the server actually applied — an empty request renders the preset, and the breadcrumb
  // must name the levels on screen rather than the ones that were asked for.
  const appliedAxes = data?.axes ?? [];
  const breadcrumb = appliedAxes.map((a) => a.name).join(' › ');

  const visibleRows = allRows.filter((r) => !isHiddenByCollapse(r.path, collapsed));
  const toggle = (path: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  // The editor stays mounted while a chain change refetches, so the control does not disappear from
  // under the cursor that just used it. Only the first load blanks the tab.
  const editor = onGroupByChange ? (
    <GroupByChips value={groupBy} onChange={onGroupByChange} defaultValue={BY_ACCOUNT_DEFAULT_GROUP_BY} />
  ) : null;

  if (isFetching && !data) {
    return <Box>{editor}<Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box></Box>;
  }
  if (isError) {
    return (
      <Box>
        {editor}
        <Alert severity="error">
          {error?.message ?? t('Finance.PlanRollup.AccountLoadFailed', { defaultValue: 'Failed to load the account view.' })}
        </Alert>
      </Box>
    );
  }
  if (!data) return <Box>{editor}</Box>;

  return (
    <Box>
      {editor}
      {data.warnings.map((w, i) => <Alert key={i} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
      {/* A deliberate, correct exclusion — reported so it is never silent, but at info severity: it is
          permanent on any tenant using internal transfer pricing, and a standing yellow alert for
          something that needs no action just teaches readers to ignore the list. */}
      {data.excludedAccountsAdvisory && (
        <Alert severity="info" sx={{ mb: 1 }}>{data.excludedAccountsAdvisory}</Alert>
      )}
      <Paper variant="outlined" sx={{ maxWidth: 720 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {/* The chain renders as the header, exactly as the pivot already does — so the column
                  always names what its rows actually are. */}
              <TableCell>
                {breadcrumb
                  || t('Finance.PlanRollup.ColAccountGroupAccount', { defaultValue: 'Account group › Account' })}
              </TableCell>
              <TableCell align="right">{t('Finance.PlanRollup.MeasureValue', { defaultValue: 'Value' })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => (
              <TableRow key={row.path} hover>
                <TableCell sx={{ pl: row.depth * 2.5 + 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: row.isGroup ? 600 : 400 }}>
                    {row.isGroup ? (
                      <Box component="span" sx={{ display: 'inline-flex', cursor: 'pointer' }} onClick={() => toggle(row.path)}>
                        {collapsed.has(row.path) ? <KeyboardArrowRight fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                      </Box>
                    ) : (
                      <Box component="span" sx={{ width: 20, display: 'inline-block' }} />
                    )}
                    <span>{row.label}{row.isGroup ? ` (${row.childCount})` : ''}</span>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: row.isGroup ? 600 : 400 }}>{fmt0(row.amount)}</TableCell>
              </TableRow>
            ))}
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('Finance.PlanRollup.EmptyAccounts', { defaultValue: 'No accounts with budget under this selection.' })}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            {/* The gross sum adds income and cost together, so it is not a result — it is only what the
                rows above visibly add up to. Kept, but no longer labelled "Total". */}
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>
                {filteredLabel(
                  t('Finance.PlanRollup.GrossSum', { defaultValue: 'Gross sum (income + costs)' }),
                  data.isShaped,
                )}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt0(data.grandTotal)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                {filteredLabel(
                  t('Finance.PlanRollup.SignedResult', { defaultValue: 'Result (income − costs)' }),
                  data.isShaped,
                )}
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: 700, color: data.signedResult < 0 ? 'error.main' : 'inherit' }}
              >
                {data.signedResultIsApproximate ? (
                  <Tooltip
                    title={t('Finance.PlanRollup.SignedResultApproximate', {
                      defaultValue:
                        'At least one account has no reporting sign of its own and its account type cannot imply one, so this figure is approximate.',
                    })}
                  >
                    <span>≈ {fmt0(data.signedResult)}</span>
                  </Tooltip>
                ) : (
                  fmt0(data.signedResult)
                )}
              </TableCell>
            </TableRow>
            {data.excludedAmount !== 0 && (
              <TableRow>
                <TableCell sx={{ color: 'text.secondary' }}>
                  {t('Finance.PlanRollup.ExcludedFromResult', { defaultValue: 'Excluded from the result' })}
                </TableCell>
                <TableCell align="right" sx={{ color: 'text.secondary' }}>{fmt0(data.excludedAmount)}</TableCell>
              </TableRow>
            )}
            <RollupUnfilteredTotalRow isShaped={data.isShaped} unfilteredTotal={unfilteredTotal} colSpan={2} />
          </TableFooter>
        </Table>
      </Paper>
    </Box>
  );
}

export default PlanRollupByAccount;
